import { LoadingSpinner, Typography } from "@shared/ui.primitives";
import type { JSX } from "react";
import { useEffect, useRef } from "react";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import {
  carouselWrapper,
  slideContent,
  swiperContainer
} from "./JourneyCarousel.css";

SwiperCore.use([Navigation]);

type MemberJourneyItem = {
  value: string;
  text: string;
};

type Props = {
  isLoading?: boolean;
  journeyItems: MemberJourneyItem[];
  title: string;
  onNext?: () => void;
  onPrev?: () => void;
};

/**
 * JourneyCarousel Component
 *
 * Displays a horizontal swiper carousel showcasing key membership stats like savings, years, or products bought.
 *
 * @component
 * @param {boolean} [isLoading=false] - Displays a loading spinner when true instead of slides.
 * @param {Array<{ value: string, text: string }>} journeyItems - List of journey items to display as slides.
 * @param {string} title - Carousel title text.
 * @param {Function} [onNext] - Callback function triggered when the next button is clicked.
 * @param {Function} [onPrev] - Callback function triggered when the previous button is clicked.
 *
 * @example
 * <JourneyCarousel
 *   isLoading={false}
 *   journeyItems={[{ value: \"£556\", text: \"Saved\" }]}
 *   title=\"Your Beauty Pie Journey\"
 *   onNext={() => console.log(\"Next clicked\")}
 *   onPrev={() => console.log(\"Previous clicked\")}
 * />
 */
export const JourneyCarousel = ({
  isLoading = false,
  journeyItems,
  title,
  onNext = () => {},
  onPrev = () => {}
}: Props): JSX.Element => {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Ensure refs are mounted before Swiper tries to read them
  }, [prevRef.current, nextRef.current]);

  return (
    <div
      className={carouselWrapper}
      data-testid="journey-carousel"
      aria-label="Beauty pie journey carousel"
    >
      {isLoading ? (
        <div className={swiperContainer}>
          <LoadingSpinner size="sm" colour="accent-medium" />
        </div>
      ) : (
        <>
          <Typography variant="subheader-4">{title}</Typography>
          <div className={swiperContainer}>
            <button
              aria-label="Previous slide"
              className={"swiper-button-prev"}
              onClick={onPrev}
              ref={prevRef}
            />

            <Swiper
              spaceBetween={20}
              slidesPerView={1}
              loop
              centeredSlides={false}
              navigation={{
                nextEl: `.swiper-button-next`,
                prevEl: `.swiper-button-prev`
              }}
            >
              {journeyItems.map((item) => (
                <SwiperSlide key={item.text}>
                  <div className={slideContent}>
                    <Typography variant="header-1">{item.value}</Typography>
                    <Typography variant="body-md">{item.text}</Typography>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            <button
              aria-label="Next slide"
              className={"swiper-button-next"}
              onClick={onNext}
              ref={nextRef}
            />
          </div>
        </>
      )}
    </div>
  );
};
